import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../layout/DashboardLayout';
import Table from '../dashboard/Table';
import DataDisplay from '../dashboard/DataDisplay';
import DashboardHeader from '../dashboard/DashboardHeader';
import TableControls from '../dashboard/TableControls';
import CustomizeFieldsDialog from '../dashboard/CustomizeFieldsDialog';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchProjects, 
  loadProjectColumnPreferences, 
  saveProjectColumnPreferences,
  deleteProject,
  fetchProject,
  archiveProject,
  unarchiveProject,
  updateProject
} from '../../store/slices/projectsSlice';
import { saveColumnOrder, fetchColumnOrder, fetchUsers } from '../../store/slices/usersSlice';
import { setSearchLoading } from '../../store/slices/searchSlice';
import { setProjectManagerFilter, clearProjectManagerFilter } from '../../store/slices/viewSlice';
import Spinner from '../common/Spinner';
import { showToast } from '../utils/Toast';
import ProjectDetailsDialog from './ProjectDetailsDialog';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { useThemeColors } from '../../hooks/useThemeColors';

const Projects = () => {
  const dispatch = useDispatch();
  const { buttonColors } = useThemeColors();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchColumn, setSearchColumn] = useState('all');
  const [isCustomizeFieldsOpen, setIsCustomizeFieldsOpen] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectDetails, setShowProjectDetails] = useState(false);

  // Redux selectors
  const { projects, loading, pagination, columnPreferences } = useSelector(state => state.projects);
  const { columnOrders, users } = useSelector(state => state.users);
  const { filters } = useSelector(state => state.view);

  // Debug log for users
  useEffect(() => {
    console.log('🔍 Projects - Users state updated:', users);
    console.log('🔍 Projects - Users count:', users?.length);
  }, [users]);

  // Project Manager Filter State
  const selectedProjectManager = filters?.projects?.selectedProjectManager || null;
  const [showProjectManagerDropdown, setShowProjectManagerDropdown] = useState(false);
  const [pmSearchTerm, setPmSearchTerm] = useState('');
  const projectManagerDropdownRef = useRef(null);

  // Date Filter State
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerDropdownRef = useRef(null);
  const suppressNextAutoLoadRef = useRef(false);

  // Update default columns to match API data structure
  const defaultColumns = [
    { id: 1, key: 'jobNumber', label: 'Job Number', name: 'Job Number', active: true, checked: true },
    // Lead Contact Information
    { id: 2, key: 'leadContact.name', label: 'Lead Contact', name: 'Lead Contact', active: true, checked: true },
    { id: 3, key: 'leadContact.phone', label: 'Lead Phone', name: 'Lead Phone', active: true, checked: true },
    { id: 4, key: 'leadContact.email', label: 'Lead Email', name: 'Lead Email', active: true, checked: true },
    // Project Manager
    { id: 5, key: 'projectManagers', label: 'Assigned To', name: 'Assigned To', active: true, checked: true },
    // Address
    { id: 6, key: 'address.street', label: 'Street', name: 'Street', active: true, checked: true },
    { id: 7, key: 'address.city', label: 'City', name: 'City', active: true, checked: true },
    { id: 8, key: 'address.state', label: 'State', name: 'State', active: true, checked: true },
    // Client Information
    { id: 9, key: 'client.name', label: 'Client Name', name: 'Client Name', active: true, checked: true },
    { id: 10, key: 'client.email', label: 'Client Email', name: 'Client Email', active: true, checked: false },
    { id: 11, key: 'client.cellPhone', label: 'Client Phone', name: 'Client Phone', active: true, checked: false },
    // Site Contact
    { id: 12, key: 'siteContact.name', label: 'Site Contact', name: 'Site Contact', active: true, checked: false },
    { id: 13, key: 'siteContact.email', label: 'Site Email', name: 'Site Email', active: true, checked: false },
    { id: 14, key: 'siteContact.cellPhone', label: 'Site Phone', name: 'Site Phone', active: true, checked: false },
    // Status and Other Fields
    { id: 15, key: 'projectStatus', label: 'Status', name: 'Status', active: true, checked: true },
    { id: 16, key: 'priority', label: 'Priority', name: 'Priority', active: true, checked: true },
    { id: 17, key: 'mapLink', label: 'Map Link', name: 'Map Link', active: true, checked: false },
    { id: 18, key: 'isArchived', label: 'Archived', name: 'Archived', active: true, checked: false },
    { id: 19, key: 'hidden', label: 'Hidden', name: 'Hidden', active: true, checked: false },
    { id: 20, key: 'createdAt', label: 'Created Date', name: 'Created Date', active: true, checked: true },
    { id: 21, key: 'lastModified', label: 'Last Modified', name: 'Last Modified', active: true, checked: false }
  ];

  // Initialize states with the full column metadata
  const [visibleColumns, setVisibleColumns] = useState(
    defaultColumns.filter(col => col.checked)
  );
  const [activeColumns, setActiveColumns] = useState(defaultColumns);

  // Helper function to get initials
  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last;
  };

  // Fetch all users on mount for PM filter
  useEffect(() => {
    console.log('🚀 Projects - Fetching users...');
    dispatch(fetchUsers()).then((result) => {
      console.log('✅ Projects - Users fetched:', result);
    }).catch((error) => {
      console.error('❌ Projects - Error fetching users:', error);
    });
  }, [dispatch]);

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (projectManagerDropdownRef.current && !projectManagerDropdownRef.current.contains(event.target)) {
        setShowProjectManagerDropdown(false);
        setPmSearchTerm('');
      }
      if (datePickerDropdownRef.current && !datePickerDropdownRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Function to get all unique project managers from all users
  const getAllProjectManagers = () => {
    console.log('🔍 Projects - getAllProjectManagers called');
    console.log('🔍 Projects - users:', users);
    console.log('🔍 Projects - users length:', users?.length);
    
    if (!users || users.length === 0) {
      console.log('⚠️ Projects - No users found or users array is empty');
      return [];
    }
    
    // Filter users to only include those with valid information
    // Note: The users from calendar-users endpoint have firstName/lastName directly on the user object
    // We need to transform them to match the expected structure with profile property
    const projectManagers = users
      .filter(user => {
        const isValid = user && user._id && user.firstName && user.lastName;
        if (!isValid) {
          console.log('⚠️ Projects - Invalid user:', user);
        }
        return isValid;
      })
      .map(user => ({
        ...user,
        profile: {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role
        }
      }));
    
    console.log('✅ Projects - Filtered project managers:', projectManagers);
    console.log('✅ Projects - Project managers count:', projectManagers.length);
    
    return projectManagers.sort((a, b) => {
      const nameA = `${a.profile.firstName || ''} ${a.profile.lastName || ''}`.toLowerCase();
      const nameB = `${b.profile.firstName || ''} ${b.profile.lastName || ''}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  // Function to filter project managers based on search term
  const getFilteredProjectManagers = () => {
    const allManagers = getAllProjectManagers();
    if (!pmSearchTerm.trim()) {
      return allManagers;
    }
    const searchLower = pmSearchTerm.toLowerCase();
    return allManagers.filter(manager => {
      const fullName = `${manager.profile?.firstName || ''} ${manager.profile?.lastName || ''}`.toLowerCase();
      const role = manager.profile?.role?.replace('_', ' ').toLowerCase() || '';
      return fullName.includes(searchLower) || role.includes(searchLower);
    });
  };

  // Helper functions for Redux project manager filter
  const handleSetProjectManager = (manager) => {
    dispatch(setProjectManagerFilter({ page: 'projects', projectManager: manager }));
    setPmSearchTerm('');
    setShowProjectManagerDropdown(false);
  };

  const handleClearProjectManager = () => {
    dispatch(clearProjectManagerFilter({ page: 'projects' }));
    setPmSearchTerm('');
    setShowProjectManagerDropdown(false);
  };

  // Helper functions for date filtering
  const getDateRangeDisplay = () => {
    const { startDate, endDate } = dateRange;
    if (!startDate && !endDate) return 'All Time';
    if (startDate && endDate) {
      return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
    }
    if (startDate) return `From ${startDate.toLocaleDateString()}`;
    if (endDate) return `Until ${endDate.toLocaleDateString()}`;
    return 'All Time';
  };

  const clearDateRange = () => {
    setDateRange({ startDate: null, endDate: null });
  };

  // Load projects when component mounts or when dependencies change
  useEffect(() => {
    if (suppressNextAutoLoadRef.current) {
      suppressNextAutoLoadRef.current = false;
      return;
    }

    loadProjects();
  }, [currentPage, itemsPerPage, sortConfig, searchTerm, searchColumn, activeTab, selectedProjectManager, dateRange, dispatch]);

  // Load preferences and set columns on mount
  useEffect(() => {
    const loadPreferences = async () => {
      console.log('🔄 Loading project preferences and column order...');
      
      try {
        // Load both column preferences and column order in parallel
        const [preferencesResult, orderResult] = await Promise.all([
          dispatch(loadProjectColumnPreferences()).unwrap(),
          dispatch(fetchColumnOrder('projects')).unwrap()
        ]);
        
        console.log('📊 Project preferences result:', preferencesResult);
        console.log('📋 Project order result:', orderResult);
        
        let columnsToProcess = defaultColumns;
        
        // First apply column preferences (checked/unchecked status)
        if (preferencesResult?.length) {
          columnsToProcess = defaultColumns.map(col => {
            const savedPref = preferencesResult.find(pref => parseInt(pref.id) === col.id);
            if (savedPref) {
              return {
                ...col,
                checked: savedPref.checked,
                active: savedPref.active
              };
            }
            return col;
          });
          console.log('✅ Applied project column preferences');
        }
        
        // Then apply column order if available
        const savedOrder = orderResult?.columnOrder || [];
        console.log('🔍 Saved project column order:', savedOrder);
        console.log('🔍 Available project column keys:', columnsToProcess.map(c => c.key));
        
        if (savedOrder && savedOrder.length > 0) {
          const orderedColumns = [];
          const columnMap = new Map(columnsToProcess.map(col => [col.key, col]));
          
          console.log('🗺️ Project column map keys:', Array.from(columnMap.keys()));
          
          // Add columns in the saved order
          savedOrder.forEach(key => {
            if (columnMap.has(key)) {
              orderedColumns.push(columnMap.get(key));
              columnMap.delete(key);
              console.log(`✅ Added project column in order: ${key}`);
            } else {
              console.log(`⚠️ Project column key not found: ${key}`);
            }
          });
          
          // Add any remaining columns that weren't in the saved order
          columnMap.forEach(col => {
            orderedColumns.push(col);
            console.log(`➕ Added remaining project column: ${col.key}`);
          });
          
          console.log('🎯 Final ordered project columns:', orderedColumns.map(c => ({ key: c.key, label: c.label })));
          
          setActiveColumns(orderedColumns);
          setVisibleColumns(orderedColumns.filter(col => col.checked));
          console.log('✅ Applied saved project column order');
        } else {
          console.log('ℹ️ No saved project column order found, using default');
          setActiveColumns(columnsToProcess);
          setVisibleColumns(columnsToProcess.filter(col => col.checked));
        }
      } catch (error) {
        console.error('❌ Failed to load project preferences:', error);
        // Use default columns on error
        setActiveColumns(defaultColumns);
        setVisibleColumns(defaultColumns.filter(col => col.checked));
      }
    };
    
    loadPreferences();
  }, [dispatch]);

  const loadProjects = async (overrides = {}) => {
    const effectiveSearchTerm = overrides.searchTerm ?? searchTerm;
    const effectiveSearchColumn = overrides.searchColumn ?? searchColumn;
    const effectivePage = overrides.page ?? (itemsPerPage === 'all' ? 1 : currentPage);
    const effectiveLimit = overrides.limit ?? (itemsPerPage === 'all' ? 1000000 : itemsPerPage);

    const params = {
      page: effectivePage,
      limit: effectiveLimit,
      ...(sortConfig.key && {
        sortField: sortConfig.key,
        sortOrder: sortConfig.direction
      }),
      ...(effectiveSearchTerm && { 
        search: effectiveSearchTerm,
        ...(effectiveSearchColumn !== 'all' && { searchField: effectiveSearchColumn })
      }),
      ...(selectedProjectManager && {
        projectManagerId: selectedProjectManager._id
      }),
      ...(dateRange.startDate && { startDate: dateRange.startDate.toISOString() }),
      ...(dateRange.endDate && { endDate: dateRange.endDate.toISOString() })
    };

    // Update tab-specific parameters
    switch (activeTab) {
      case 'ready_to_schedule':
        params.projectStatus = 'ready_to_schedule';
        break;
      case 'completed':
        params.projectStatus = 'completed';
        break;
      case 'on_hold':
        params.projectStatus = 'on_hold';
        break;
      case 'cancelled':
        params.projectStatus = 'cancelled';
        break;
      case 'archived':
        params.showArchived = 'true';
        break;
      case 'active':
      default:
        params.projectStatus = 'active';
        break;
    }

    return dispatch(fetchProjects(params)).unwrap();
  };

  const handleSearch = async (searchValue, columnKey = 'all') => {
    try {
      dispatch(setSearchLoading(true));

      // We fetch manually below, so suppress the next dependency-driven auto fetch.
      suppressNextAutoLoadRef.current = true;
      setSearchTerm(searchValue);
      setSearchColumn(columnKey);
      setCurrentPage(1);
      
      // For now, we'll just use the backend search since column-specific searching 
      // isn't supported on the server yet
      const result = await loadProjects({
        searchTerm: searchValue,
        searchColumn: columnKey,
        page: 1
      });
      
      // If we have results and a specific column was selected, filter client-side
      const loadedProjects = result?.projects || [];
      if (searchValue && columnKey !== 'all' && loadedProjects.length > 0) {
        // Apply client-side filtering for specific column
        const filtered = loadedProjects.filter(project => {
          // Handle nested properties like 'client.name'
          if (columnKey.includes('.')) {
            const keys = columnKey.split('.');
            let value = project;
            for (const key of keys) {
              if (!value || typeof value !== 'object') return false;
              value = value[key];
            }
            return value && String(value).toLowerCase().includes(searchValue.toLowerCase());
          } 
          // Handle special cases like projectManagers array
          else if (columnKey === 'projectManagers') {
            const managers = project.projectManagers || [];
            return managers.some(pm => 
              (pm.profile?.firstName + ' ' + pm.profile?.lastName)
                .toLowerCase()
                .includes(searchValue.toLowerCase())
            );
          }
          // Regular properties
          else {
            return project[columnKey] && 
              String(project[columnKey]).toLowerCase().includes(searchValue.toLowerCase());
          }
        });
        
        // Update the state with filtered projects
        // This is a workaround since the backend doesn't support column-specific searching
        dispatch({
          type: 'projects/fetchProjects/fulfilled',
          payload: {
            projects: filtered,
            pagination: {
              ...(result?.pagination || pagination),
              total: filtered.length
            }
          }
        });
      }
    } finally {
      dispatch(setSearchLoading(false));
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleColumnToggle = (columnId) => {
    console.log('Projects - Toggling column:', columnId);
    const updatedColumns = activeColumns.map(col => {
      if (col.id === columnId) {
        console.log('Toggling column from:', col.checked, 'to:', !col.checked);
        return { ...col, checked: !col.checked };
      }
      return col;
    });
    console.log('Updated columns:', updatedColumns);
    setActiveColumns(updatedColumns);
  };

  const handleApplyColumns = async (selectedColumns) => {
    try {
      // Ensure columns have all required properties
      const columnsWithKeys = selectedColumns.map(col => {
        const originalCol = defaultColumns.find(defaultCol => defaultCol.id === parseInt(col.id));
        return {
          ...col,
          key: originalCol?.key || col.key,
          label: originalCol?.label || col.label,
          name: originalCol?.name || col.name
        };
      });

      // Make sure we're sending an array
      await dispatch(saveProjectColumnPreferences(
        Array.isArray(columnsWithKeys) ? columnsWithKeys : []
      )).unwrap();
      
      const updatedColumns = columnsWithKeys.filter(col => col.checked);
      setVisibleColumns(updatedColumns);
      setActiveColumns(columnsWithKeys);
      setIsCustomizeFieldsOpen(false);
    } catch (error) {
      console.error('Failed to save preferences:', error);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setCurrentPage(1);
    setSortConfig({ key: null, direction: 'asc' });
    dispatch(clearProjectManagerFilter({ page: 'projects' })); // Reset project manager filter when changing tabs
  };

  const handleCheckRow = (id) => {
    setSelectedProjects(prev => {
      if (prev.includes(id)) {
        return prev.filter(projectId => projectId !== id);
      }
      return [...prev, id];
    });
  };

  const handleCheckAll = () => {
    if (selectedProjects.length === projects.length) {
      setSelectedProjects([]);
    } else {
      setSelectedProjects(projects.map(project => project._id));
    }
  };

  // Add formatDisplayValue function
  const formatDisplayValue = (value, type) => {
    if (!value) return '';
    
    const str = String(value);
    
    switch (type) {
      case 'projectStatus':
        return str.split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      
      case 'priority':
        return str.charAt(0).toUpperCase() + str.slice(1);
      
      default:
        return str;
    }
  };

  // Transform project data based on visible columns
  const transformProjectData = (project) => {
    const transformed = {};
    visibleColumns.forEach(column => {
      let value = column.key.split('.').reduce((obj, key) => obj?.[key], project);

      // Handle special cases
      switch (column.key) {
        case 'projectManagers':
          value = project.projectManagers?.map(pm => 
            `${pm.profile?.firstName} ${pm.profile?.lastName}`
          ).join(', ') || 'N/A';
          break;

        case 'leadContact.name':
          value = project.leadContact?.name || 'N/A';
          break;

        case 'leadContact.phone':
          value = project.leadContact?.phone || 'N/A';
          break;

        case 'leadContact.email':
          value = project.leadContact?.email || 'N/A';
          break;

        case 'projectStatus':
          value = formatDisplayValue(value, 'projectStatus');
          break;

        case 'priority':
          value = formatDisplayValue(value, 'priority');
          break;

        case 'createdAt':
        case 'lastModified':
          value = value ? new Date(value).toLocaleString('en-US', {
          timeZone: 'America/Los_Angeles',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        }) : 'N/A';
          break;

        case 'isArchived':
        case 'hidden':
          value = value ? 'Yes' : 'No';
          break;

        case 'mapLink':
          value = value ? (
            <a 
              href={value} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-blue-600 hover:text-blue-800"
              onClick={(e) => e.stopPropagation()}
            >
              View Map
            </a>
          ) : 'N/A';
          break;

        default:
          value = value || 'N/A';
      }

      transformed[column.key] = value;
    });
    transformed.id = project._id;
    transformed._id = project._id;
    return transformed;
  };

  // Helper function to format project status
  const formatProjectStatus = (status) => {
    const statusColors = {
      'active': 'bg-green-100 text-green-800',
      'ready_to_schedule': 'bg-purple-100 text-purple-800',
      'on_hold': 'bg-yellow-100 text-yellow-800',
      'completed': 'bg-blue-100 text-blue-800',
      'cancelled': 'bg-red-100 text-red-800',
      'archived': 'bg-gray-100 text-gray-800'
    };

    const statusLabels = {
      'active': 'Active',
      'ready_to_schedule': 'Ready to Schedule',
      'on_hold': 'On Hold',
      'completed': 'Completed',
      'cancelled': 'Cancelled',
      'archived': 'Archived'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  // Helper function to format priority
  const formatPriority = (priority) => {
    const priorityColors = {
      'low': 'bg-gray-100 text-gray-800',
      'medium': 'bg-blue-100 text-blue-800',
      'high': 'bg-orange-100 text-orange-800',
      'very high': 'bg-red-100 text-red-800'
    };

    const priorityLabels = {
      'low': 'Low',
      'medium': 'Medium',
      'high': 'High',
      'very high': 'Very High'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityColors[priority] || 'bg-gray-100 text-gray-800'}`}>
        {priorityLabels[priority] || priority}
      </span>
    );
  };

  const handleDeleteClick = () => {
    if (selectedProjects.length === 0) {
      showToast('Please select a project to delete', 'warning');
      return;
    }
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      for (const projectId of selectedProjects) {
        await dispatch(deleteProject(projectId)).unwrap();
      }
      setShowDeleteDialog(false);
      setSelectedProjects([]);
      showToast('Project(s) deleted successfully', 'success');
      loadProjects();
    } catch (error) {
      console.error('Failed to delete project:', error);
      showToast('Failed to delete project(s)', 'error');
    }
  };

  const handleRowClick = async (project) => {
    try {
      if (!project?._id && !project?.id) {
        showToast('Invalid project selected', 'error');
        return;
      }
      
      const projectId = project._id || project.id;
      await dispatch(fetchProject(projectId)).unwrap();
      setSelectedProject(project);
      setShowProjectDetails(true);
    } catch (error) {
      console.error('Failed to fetch project details:', error);
      showToast('Failed to fetch project details', 'error');
    }
  };

  // Add new handlers for archive/unarchive
  const handleArchive = async () => {
    try {
      for (const projectId of selectedProjects) {
        await dispatch(archiveProject(projectId)).unwrap();
      }
      setSelectedProjects([]);
      showToast('Projects archived successfully', 'success');
      loadProjects();
    } catch (error) {
      showToast(error || 'Failed to archive projects', 'error');
    }
  };

  const handleUnarchive = async () => {
    try {
      for (const projectId of selectedProjects) {
        await dispatch(unarchiveProject(projectId)).unwrap();
      }
      setSelectedProjects([]);
      showToast('Projects unarchived successfully', 'success');
      loadProjects();
    } catch (error) {
      showToast(error || 'Failed to unarchive projects', 'error');
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleCellUpdate = async (projectId, field, value) => {
    try {
      await dispatch(updateProject({ 
        id: projectId, 
        updates: { [field]: value }
      })).unwrap();
      
      // Refresh the projects data
      loadProjects();
    } catch (error) {
      console.error('Failed to update project:', error);
    }
  };

  // Add column reorder handler
  const handleColumnReorder = async (newColumnOrder) => {
    try {
      console.log('🔄 Reordering project columns:', newColumnOrder.map(c => ({ key: c.key, label: c.label })));
      
      // Update local state immediately
      setActiveColumns(newColumnOrder);
      setVisibleColumns(newColumnOrder.filter(col => col.checked));
      
      // Save to backend
      const columnKeys = newColumnOrder.map(col => col.key);
      console.log('💾 Saving project column order:', columnKeys);
      await dispatch(saveColumnOrder({ type: 'projects', columnOrder: columnKeys })).unwrap();
      console.log('✅ Project column order saved successfully');
    } catch (error) {
      console.error('❌ Failed to save project column order:', error);
      // Revert on error
      setActiveColumns(activeColumns);
      setVisibleColumns(activeColumns.filter(col => col.checked));
    }
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col h-screen">
        <DashboardHeader 
          hideCreateLead={true}
          onCustomizeFields={() => setIsCustomizeFieldsOpen(true)}
          onSearch={handleSearch}
          searchTerm={searchTerm}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          selectedItems={selectedProjects}
          hideActions={false}
          hideTabBar={false}
          onDelete={handleDeleteClick}
          tabs={[
            { id: 'active', label: 'Active Projects' },
            { id: 'ready_to_schedule', label: 'Ready to Schedule' },
            { id: 'on_hold', label: 'On Hold' },
            { id: 'completed', label: 'Completed' },
            { id: 'cancelled', label: 'Cancelled' },
            { id: 'archived', label: 'Archives' },
          ]}
          items={projects}
          visibleColumns={visibleColumns}
        />
        <div className="flex-1 overflow-hidden p-4 flex flex-col">
          <div className="mb-4 flex justify-between items-center">
            <span className="text-lg text-gray-600">{pagination?.total || 0} Projects</span>
            {activeTab === 'archived' && selectedProjects.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleUnarchive}
                  className="px-3 py-1 text-sm text-white rounded transition-colors"
                  style={{
                    backgroundColor: buttonColors.primary,
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = buttonColors.primaryHover}
                  onMouseLeave={(e) => e.target.style.backgroundColor = buttonColors.primary}
                >
                  Unarchive Selected
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-32">
              <Spinner size="xl" color="blue" />
            </div>
          ) : (
            <>
              {(!projects || projects.length === 0) ? (
                <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                  <DocumentIcon className="w-16 h-16 mb-4" />
                  <p className="text-lg font-medium">No projects found</p>
                  <p className="text-sm">Projects created from leads will appear here</p>
                </div>
              ) : (
                <div className="flex-1 min-h-0 flex flex-col">
                  <div className="flex-1 min-h-0">
                    <DataDisplay 
                      columns={visibleColumns}
                      data={(projects || []).map(transformProjectData)}
                      onSort={handleSort}
                      sortConfig={sortConfig}
                      onCheckAll={handleCheckAll}
                      onCheckRow={handleCheckRow}
                      selectedRows={selectedProjects}
                      onRowClick={handleRowClick}
                      onUpdateCell={handleCellUpdate}
                      onColumnReorder={handleColumnReorder}
                      title="Projects"
                      // Remove pagination from DataDisplay
                      pagination={null}
                      // Project Manager Filter Props
                      showProjectManagerFilter={true}
                      selectedProjectManager={selectedProjectManager}
                      showProjectManagerDropdown={showProjectManagerDropdown}
                      setShowProjectManagerDropdown={setShowProjectManagerDropdown}
                      pmSearchTerm={pmSearchTerm}
                      setPmSearchTerm={setPmSearchTerm}
                      handleSetProjectManager={handleSetProjectManager}
                      handleClearProjectManager={handleClearProjectManager}
                      projectManagerDropdownRef={projectManagerDropdownRef}
                      getAllProjectManagers={getAllProjectManagers}
                      getFilteredProjectManagers={getFilteredProjectManagers}
                      getInitials={getInitials}
                      // Date Filter Props
                      showDateFilter={true}
                      dateRange={dateRange}
                      setDateRange={setDateRange}
                      showDatePicker={showDatePicker}
                      setShowDatePicker={setShowDatePicker}
                      datePickerDropdownRef={datePickerDropdownRef}
                      getDateRangeDisplay={getDateRangeDisplay}
                      clearDateRange={clearDateRange}
                    />
                  </div>
                  
                  {/* Separate Pagination Section */}
                  {pagination && pagination.total > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200 bg-white rounded-lg">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        {/* Items per page selector */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                          <div className="flex items-center gap-2">
                            <label className="text-sm text-gray-600 whitespace-nowrap">Items per page:</label>
                            <select
                              value={itemsPerPage === 'all' ? 'all' : itemsPerPage}
                              onChange={(e) => {
                                const value = e.target.value === 'all' ? 'all' : parseInt(e.target.value);
                                handleItemsPerPageChange(value);
                              }}
                              className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 min-h-[40px]"
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                              <option value="all">All</option>
                            </select>
                          </div>
                          <div className="text-sm text-gray-600">
                            {itemsPerPage === 'all' 
                              ? `Showing all ${pagination.total} projects`
                              : `Showing ${Math.min((currentPage - 1) * itemsPerPage + 1, pagination.total)}-${Math.min(currentPage * itemsPerPage, pagination.total)} of ${pagination.total} projects`
                            }
                          </div>
                        </div>

                        {/* Page navigation */}
                        {itemsPerPage !== 'all' && pagination.pages > 1 && (
                          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <div className="text-sm text-gray-600 text-center sm:text-left">
                              Page {currentPage} of {pagination.pages}
                            </div>
                            <div className="flex justify-center sm:justify-start space-x-2">
                              <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage <= 1}
                                className={`px-4 py-2 min-h-[44px] text-sm font-medium rounded-lg border transition-colors
                                  ${currentPage <= 1 
                                    ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 active:bg-gray-100'
                                  }`}
                              >
                                Previous
                              </button>
                              <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage >= pagination.pages}
                                className={`px-4 py-2 min-h-[44px] text-sm font-medium rounded-lg border transition-colors
                                  ${currentPage >= pagination.pages
                                    ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 active:bg-gray-100'
                                  }`}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <CustomizeFieldsDialog 
        isOpen={isCustomizeFieldsOpen}
        onClose={() => setIsCustomizeFieldsOpen(false)}
        columns={activeColumns}
        onToggleColumn={handleColumnToggle}
        onApply={handleApplyColumns}
        type="projects"
      />

      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Delete Project{selectedProjects.length > 1 ? 's' : ''}
            </h3>
            <p className="text-gray-500 mb-6">
              Are you sure you want to delete {selectedProjects.length > 1 ? 'these projects' : 'this project'}? 
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                onClick={() => setShowDeleteDialog(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg"
                onClick={handleDeleteConfirm}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showProjectDetails && selectedProject && (
        <ProjectDetailsDialog
          isOpen={showProjectDetails}
          onClose={() => {
            setShowProjectDetails(false);
            setSelectedProject(null);
          }}
          project={selectedProject}
        />
      )}
    </DashboardLayout>
  );
};

export default Projects; 